import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { useLocation } from '@reach/router';
import pt from 'prop-types';
import { combine } from '/src/helpers/styles';
import { queryParamKeyMicrocopyKeyMap } from '/src/helpers/filters';
import { copyToClipboard } from '/src/helpers/copyToClipboard';
import { getCopy } from '/src/helpers/copy';
import useScrollPosition from '/src/hooks/useScrollPosition';
import useWindowSize from '/src/hooks/useWindowSize';
import { usePagination } from '/src/hooks/usePagination';
import Container from '/src/modules/container';
import Logo from '../../../../images/logo.svg';
import { MicrocopyContext } from '/src/context/microcopy.context';
import { breakpoints } from '/src/utils/constants';
import Text from '../../../ui/text';
import FilterTag from '../../../ui/filterTag';
import CustomScrollable from '../../../ui/customScrollable';
import Icon from '../../../ui/icon';
import WDIFilterSet from '../wdiFilterSet';
import WDIFiltersModal from '../wdiFiltersModal';
import * as styles from './resultsList.module.scss';

const STICKY_HEADER_HEIGHT = 140;
const filterOrderByKey = [
  'profileTypes',
  'continents',
  'countries',
  'keywords',
];
const openedFilters = ['profileTypes', 'continents', 'countries', 'keywords'];
const radioFiltersKeys = [
  'profileTypes',
  'continents',
  'countries',
  'keywords',
];

const getActiveFilters = (queryFilters, visibleFilters) => {
  const allCountries = visibleFilters?.continents
    .map((continent) => continent.countries)
    .flat();

  return Object.entries(queryFilters)
    .filter(
      ([key, value]) => value && (key === 'profileTypes' || key === 'keywords')
    )
    .map(([key, value]) => ({
      key,
      id: value,
      label: visibleFilters?.[key]?.find((el) => el.value === value)?.label,
    }))
    .concat(
      queryFilters.continents
        ? {
            key: 'continents',
            id: queryFilters.continents,
            label: visibleFilters?.continents.find((continent) =>
              continent.countries.find(
                (el) =>
                  el.value === queryFilters.continents &&
                  el.queryParam === 'continents'
              )
            )?.label,
          }
        : []
    )
    .concat(
      queryFilters.countries
        ? {
            key: 'countries',
            id: queryFilters.countries,
            label: allCountries?.find(
              (el) => el.value === queryFilters.countries
            )?.label,
          }
        : []
    );
};

const WDIResultsList = ({
  whiteBackground,
  noResultsLabelKey,
  itemRenderer: ItemRenderer,
  resultsContainerClassName,
  borderInHeader,
  noFilters,
  hideFilterOnLoad = false,
  queryFilters,
  hideSort,
  type,
  results,
  totalCount,
  resultsPerPage,
  visibleFilters,
}) => {
  const [activeFilters, setActiveFilters] = useState(
    getActiveFilters(queryFilters, visibleFilters)
  ); // queryFilters that match visibleFilters
  const [tempActiveFilters, setTempActiveFilters] = useState([]); // used to hold temporary filters for mobile
  const microcopy = useContext(MicrocopyContext);
  const windowSize = useWindowSize();
  const { scrollY } = useScrollPosition();
  const [menuBottomY, setMenuBottomY] = useState(140);
  const [isSmallScreen, setIsSmallScreen] = useState(
    windowSize.width < breakpoints.large
  );
  const [filtersOpen, setFiltersOpen] = useState(!hideFilterOnLoad);
  const [windowLocation, setWindowLocation] = useState(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const anchorRef = useRef();
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('hideFilter', filtersOpen ? 0 : 1);
    window.history.replaceState(
      null,
      '',
      location.pathname + `?${queryParams.toString()}` + location.hash
    );
  }, [filtersOpen]);

  useEffect(() => {
    setWindowLocation(location);
  }, []);

  useEffect(() => {
    setActiveFilters(getActiveFilters(queryFilters, visibleFilters));
  }, [queryFilters, visibleFilters]);

  useEffect(() => {
    let bottom;
    const isDesktopMenuHidden = document.querySelector(
      '[class^="header-module--header"][class*="notVisible"]'
    );
    const menu = document.querySelector('[class^="header-module--header"]');

    const menuBottomX = menu?.getBoundingClientRect().bottom;

    if (!isSmallScreen) {
      bottom = isDesktopMenuHidden ? 0 : STICKY_HEADER_HEIGHT;
    } else {
      bottom = menuBottomX;
    }
    if (bottom !== menuBottomY) {
      setMenuBottomY(bottom);
    }
  }, [scrollY, isSmallScreen, anchorRef.current]);

  const renderResults = () =>
    results.map((el, index) => (
      <ItemRenderer
        {...el}
        data={el}
        index={index}
        key={index}
        cappedWidth={!filtersOpen}
      />
    ));

  // anchorRef is used to account for the sticky header
  useEffect(() => {
    if (location.hash === '#resultList' && anchorRef.current) {
      anchorRef.current.scrollIntoView();
    }
  }, [anchorRef.current, location.hash]);

  const renderPageLink = (pageNum, text, disabled) => {
    const searchParams = new URLSearchParams(windowLocation?.search);
    searchParams.set('page', pageNum);
    searchParams.set('hideFilter', filtersOpen ? 0 : 1);
    searchParams.delete('profileId');

    searchParams.sort();

    return (
      <a
        href={`?${searchParams}#resultList`}
        className={combine(
          styles.paginationItem,
          pageNum === queryFilters.page && !text && styles.active,
          disabled && styles.disabled
        )}
      >
        <Text as="span">{text || pageNum}</Text>
      </a>
    );
  };

  // calculate search params for given filter (key)
  const buildSearchParamsAfterRemove = useMemo(
    () =>
      ({ key, val, baseSearchParams = null }) => {
        let searchParams =
          baseSearchParams || new URLSearchParams(windowLocation?.search);
        const searchParamsFromKey = searchParams.getAll(key);

        searchParams.delete(key);
        if (searchParamsFromKey.length > 1) {
          for (let value of searchParamsFromKey) {
            if (String(value) !== String(val)) {
              searchParams.append(key, value);
            }
          }
        }

        searchParams.sort();

        return searchParams;
      },
    [windowLocation?.search]
  );

  const renderPagination = () => {
    const totalPages = Math.ceil(totalCount / resultsPerPage);

    const items = usePagination({
      currentPage: queryFilters.page,
      count: totalPages,
      siblingCount: isSmallScreen ? 1 : 2,
    });

    return items.map((item) => {
      switch (item.type) {
        case 'start-ellipsis':
        case 'end-ellipsis':
          return (
            <span className={styles.paginationItem} key={item.type}>
              &hellip;
            </span>
          );
        case 'page':
          return !item.disabled && renderPageLink(item.page);
        case 'previous':
          return renderPageLink(queryFilters.page - 1, '<', item.disabled);
        case 'next':
          return renderPageLink(queryFilters.page + 1, '>', item.disabled);
      }
    });
  };

  useEffect(() => {
    if (windowSize.width >= breakpoints.large) {
      setIsSmallScreen(false);
    } else {
      setIsSmallScreen(true);
    }
  }, [windowSize]);

  useEffect(() => {
    if (isSmallScreen) {
      setFiltersOpen(false);
    }
  }, [isSmallScreen]);

  const renderFilters = () =>
    visibleFilters &&
    Object.entries(visibleFilters).map(([key, val]) => (
      <WDIFilterSet
        withTopBorder={!borderInHeader}
        key={key}
        label={key}
        visibleFilters={val}
        queryFilters={queryFilters}
        isRadio={radioFiltersKeys?.includes(key)}
        openAtStart={!openedFilters.length || openedFilters.includes(key)}
      />
    ));

  const onFilterModalClose = () => {
    setFiltersOpen(false);
  };

  const handleShare = async () => {
    const resultListLink = `${location.href}${
      location.hash ? '' : '#resultList'
    }`;
    if (navigator.share !== undefined) {
      navigator.share({
        title: document.title,
        text: document.title,
        url: resultListLink,
      });
    } else {
      copyToClipboard(resultListLink);
      setCopiedToClipboard(true);

      setTimeout(() => {
        setCopiedToClipboard(false);
      }, [4000]);
    }
  };

  return (
    <div data-nosnippet>
      <div
        ref={anchorRef}
        style={{
          position: 'relative',
          top: `-${menuBottomY}px`,
        }}
      />
      {!noFilters && (
        <>
          <div
            className={combine(
              styles.backdrop,
              (!filtersOpen || !isSmallScreen) && styles.hidden
            )}
          ></div>
          <WDIFiltersModal
            visibleFilters={visibleFilters}
            queryFilters={queryFilters}
            activeFilters={activeFilters}
            buildSearchParamsAfterRemove={buildSearchParamsAfterRemove}
            onFilterSetChange={() => {}}
            isOpen={filtersOpen && isSmallScreen}
            onClose={onFilterModalClose}
            hideSort={hideSort}
            filterOrderByKey={filterOrderByKey}
            radioFiltersKeys={radioFiltersKeys}
            type={type}
          />
        </>
      )}
      <div
        className={combine(
          styles.outerContainer,
          whiteBackground && styles.whiteBg,
          filtersOpen && isSmallScreen && styles.hidden
        )}
      >
        <Container
          className={combine(
            styles.headerContainer,
            whiteBackground && styles.whiteBg
          )}
          style={{
            top: `${menuBottomY}px`,
          }}
          id="resultList"
        >
          <>
            <div className={styles.wdiHeader}>
              <Logo width={50} height={50} className={styles.logo} />

              <div className={styles.textContent}>
                <Text as="h1" type="h3">
                  {getCopy('results.ranking.headline', microcopy)}
                </Text>
                <Text as="span" type="p2">
                  {totalCount} {getCopy('results.ranking.winners', microcopy)}
                </Text>
              </div>
            </div>
            {!noFilters && (
              <div
                className={combine(
                  styles.header,
                  borderInHeader && styles.withBorder,
                  whiteBackground && styles.whiteBg
                )}
              >
                <div className={styles.actions}>
                  <>
                    <button
                      className={styles.button}
                      onClick={() => setFiltersOpen(!filtersOpen)}
                    >
                      <Icon name="sliders-h" className={styles.icon} />
                      <Text as="span" type="infotext">
                        {filtersOpen
                          ? getCopy('results.hideFilter', microcopy)
                          : getCopy('results.showFilter', microcopy)}
                      </Text>
                    </button>
                    <Text as="span" className={styles.separator}>
                      |
                    </Text>
                    <a
                      href={windowLocation?.pathname + '#resultList'}
                      className={styles.button}
                    >
                      <Text as="span" type="infotext" className={styles.reset}>
                        {getCopy('results.reset', microcopy)}
                      </Text>
                    </a>
                    <Text as="span" className={styles.separator}>
                      |
                    </Text>
                    {copiedToClipboard ? (
                      <div className={styles.copiedToClipboard}>
                        <Icon name="copy" className={styles.icon} />
                        <Text as="span" type="infotext">
                          {getCopy('results.copiedToClipboard', microcopy)}
                        </Text>
                      </div>
                    ) : (
                      <button className={styles.button} onClick={handleShare}>
                        <Icon name="share-nodes" className={styles.icon} />
                        <Text as="span" type="infotext">
                          {getCopy('results.share', microcopy)}
                        </Text>
                      </button>
                    )}
                  </>
                </div>
              </div>
            )}
          </>
        </Container>
        <Container>
          <div className={styles.main}>
            <div
              className={combine(styles.filters, filtersOpen && styles.showing)}
            >
              <CustomScrollable vertical className={styles.filtersScrollable}>
                <>
                  {activeFilters
                    .filter((el) => !['categories', 'sort'].includes(el.key))
                    .map((filter) => {
                      const searchParams = buildSearchParamsAfterRemove({
                        key: filter.key,
                        val: filter.value,
                      }).toString();
                      return (
                        <FilterTag
                          key={`${filter.key}-${filter.value}`}
                          text={
                            queryParamKeyMicrocopyKeyMap[filter.key]
                              ? `filters.${queryParamKeyMicrocopyKeyMap[
                                  filter.key
                                ]?.toLowerCase()}.${filter.id}`
                              : filter.label || filter.name
                          }
                          className={styles.filterTag}
                          isLocked={filter.locked}
                          removeLink={`?${searchParams}#resultList`}
                          filterKey={filter.key}
                          filterId={filter.id}
                          data-test={`filtertag-${filter.key}-${filter.value}`}
                          type={type}
                        />
                      );
                    })}
                  {renderFilters()}
                </>
              </CustomScrollable>
            </div>
            {results?.length ? (
              <div className={combine(styles.w100)}>
                <div
                  className={combine(
                    ItemRenderer && resultsContainerClassName
                      ? resultsContainerClassName
                      : styles.results,
                    (borderInHeader || noFilters) && styles.paddedResults
                  )}
                >
                  <div className={styles.tableHeader}>
                    <Text as="span" type="metanavi" variant="semibold">
                      {getCopy('results.ranking.ranking', microcopy)}
                    </Text>
                    <Text as="span" type="metanavi" variant="semibold">
                      {getCopy('results.ranking.company', microcopy)}
                    </Text>
                    <Text as="span" type="metanavi" variant="semibold">
                      {getCopy('results.ranking.points', microcopy)}
                    </Text>
                  </div>
                  {renderResults()}
                </div>
              </div>
            ) : (
              <div className={combine(styles.noResults, styles.paddedResults)}>
                <Text as="h3" type="h3" className={styles.text}>
                  {getCopy(noResultsLabelKey, microcopy)}
                </Text>
              </div>
            )}
          </div>
        </Container>
        {totalCount > resultsPerPage && (
          <div className={styles.pagination}>{renderPagination()}</div>
        )}
      </div>
    </div>
  );
};

export default WDIResultsList;

WDIResultsList.propTypes = {
  filtersHook: pt.func.isRequired, // see winnerFilters
  teaserType: pt.oneOf(['design', 'company']).isRequired,
  countLabelKey: pt.string,
  noResultsLabelKey: pt.string.isRequired,
  itemRenderer: pt.func,
  whiteBackground: pt.bool,
  borderInHeader: pt.bool,
  onTotalCountUpdated: pt.func,
  hideActiveFilter: pt.bool,
  hideFilterOnLoad: pt.bool,
  onTypeChange: pt.func,
  tags: pt.arrayOf(
    pt.shape({
      tag: pt.string,
      name: pt.string,
      behaviour: pt.string,
    })
  ),
  type: pt.string,
};

WDIResultsList.defaultProps = {
  countLabelKey: 'results.projects',
  noResultsLabelKey: 'results.generic.noResults',
  teaserType: 'design',
  itemRenderer: undefined,
  whiteBackground: false,
  borderInHeader: false,
  onTotalCountUpdated: () => {},
  hideActiveFilter: false,
  hideFilterOnLoad: false,
  onTypeChange: null,
  tags: [],
  type: '',
};
